import type React from 'react';

import type { SiteType } from '@gitbook/api-client';
import * as api from '@gitbook/api-client';
import {
    getFeaturesPerSiteType,
    SITE_FEATURES,
    type SiteFeatureDefinition,
} from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';

/**
 * Different lists of features used for Billing and Plans modals.
 */

/**
 * List of features unique to a given siteType. Can be passed a feature key to exclude.
 */
export function SiteTypeFeaturesList({
    triggerFeatureKey,
    siteType,
    icon,
}: {
    triggerFeatureKey?: api.SiteFeatureKey;
    siteType: SiteType;
    icon?: React.ReactElement;
}) {
    const features = getFeaturesPerSiteType()[siteType];

    // Filter out a feature if a key is provided that should be removed from the list
    const filteredFeatures = triggerFeatureKey
        ? features.filter((feature) => feature.key !== triggerFeatureKey)
        : features;

    return (
        <div className="flex flex-col gap-2">
            {filteredFeatures.map((feature) => (
                <Feature label={feature.title} icon={icon} key={feature.key} />
            ))}
        </div>
    );
}

const ENHANCE_ULTIMATE_FEATURES = [
    {
        key: 'integration-azure',
        title: 'Azure integration',
        description: 'Connect your Azure AD to your GitBook site.',
    },
    {
        key: 'integration-auth0',
        title: 'Auth0 integration',
        description: 'Connect your Auth0 account to your GitBook site.',
    },
    {
        key: 'integration-okta',
        title: 'Okta integration',
        description: 'Connect your Okta account to your GitBook site.',
    },
    {
        key: 'integration-aws-cognito',
        title: 'AWS cognito integration',
        description: 'Connect your AWS Cognito account to your GitBook site.',
    },
    {
        key: 'integration-custom-backend',
        title: 'Custom backend support',
        description: 'Contact our team to get support for your custom backend.',
    },
];

/**
 * List of the _new_ features that users will gain when they upgrade from one to another site type.
 */
export function GainedFeaturesList({
    triggerFeatureKey,
    from,
    to,
}: {
    triggerFeatureKey?: api.SiteFeatureKey;
    from: SiteType;
    to: SiteType;
}) {
    const features: Array<SiteFeatureDefinition | (typeof ENHANCE_ULTIMATE_FEATURES)[number]> =
        SITE_FEATURES.filter((feature) => feature[to] && !feature[from]);

    /* Filter out a feature if a key is provided that should be removed from the list */
    const filteredFeatures = triggerFeatureKey
        ? features.filter((feature) => feature.key !== triggerFeatureKey)
        : features;

    /* When the dialog is showing an upgrade to Ultimate and... */
    if (to === api.SiteType.Ultimate) {
        /* ...they're upgrading from Premium and not from VA */
        if (
            from === api.SiteType.Premium &&
            triggerFeatureKey !== api.SiteFeatureKey.SitesVisitorAuthentication
        ) {
            /* ...fill up the list with Ultimate perks around VA */
            filteredFeatures.push(...ENHANCE_ULTIMATE_FEATURES);
        }
        /* ...they're upgrading from Basic */
        if (from === api.SiteType.Basic) {
            /* ...Ultimate features show at the top as the list is long */
            const ultimateExclusiveFeatures = getFeaturesPerSiteType()[api.SiteType.Ultimate].map(
                (feature) => feature.key as api.SiteFeatureKey
            );

            filteredFeatures.sort((a, b) => {
                const isAExclusiveToUltimate = ultimateExclusiveFeatures.includes(
                    a.key as api.SiteFeatureKey
                );
                const isBExclusiveToUltimate = ultimateExclusiveFeatures.includes(
                    b.key as api.SiteFeatureKey
                );
                if (isAExclusiveToUltimate && !isBExclusiveToUltimate) return -1;
                if (!isAExclusiveToUltimate && isBExclusiveToUltimate) return 1;
                return 0;
            });
        }
    }

    return (
        <>
            {filteredFeatures.map((feature) => (
                <Feature label={feature.title} key={feature.key} />
            ))}
        </>
    );
}

/**
 * List of features that are lost when canceling / downgrading to another site type.
 */
export function DowngradeLostFeaturesList({ from, to }: { from: SiteType; to: SiteType }) {
    const features = SITE_FEATURES.filter((feature) => feature[from] && !feature[to]);

    return (
        <div className="flex flex-col gap-2">
            {features.map((feature) => (
                <Feature
                    label={feature.title}
                    key={feature.key}
                    icon={<Icon.X size={18} className="m-nudge-icon shrink-0 text-danger" />}
                />
            ))}
        </div>
    );
}

export function Feature({ label, icon }: { label: string; icon?: React.ReactElement }) {
    return (
        <div className="flex items-center gap-1 text-muted">
            {icon ? icon : <Icon.Check size={16} className="shrink-0" />}
            <span>{label}</span>
        </div>
    );
}
